import React, { useState, useEffect } from "react";
import { Search, Eye, Edit2, Download, Printer, FileSpreadsheet, Copy, CheckCircle, AlertCircle, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { db, storage } from "../../firebase";
import { ref, push, set, onValue } from "firebase/database";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";

export default function PassbookUpdate() {
  const [formData, setFormData] = useState({
    workName: "",
    lotteryNo: "",
    price: "",
    department: "",
    duration: "",
    completionDate: "",
    file: null,
  });

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('currentUser')) || {};

  useEffect(() => {
    if (currentUser._node && currentUser._key) {
      const workCompleteRef = ref(db, `${currentUser._node}/${currentUser._key}/work complete`);
      const unsub = onValue(workCompleteRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const list = Object.entries(data).map(([key, value]) => ({
            ...value,
            id: key
          }));
          setTableData(list);
        } else {
          setTableData([]);
        }
      });
      return () => unsub();
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleEdit = (item) => {
    setFormData({
      workName: item.workName,
      lotteryNo: item.lotteryNo,
      price: item.price,
      department: item.department,
      duration: item.duration,
      completionDate: item.completionDate,
      file: null, // Keep existing file unless a new one is selected
    });
    setIsEditing(true);
    setEditId(item.lotteryNo);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditId(null);
    setFormData({
      workName: "",
      lotteryNo: "",
      price: "",
      department: "",
      duration: "",
      completionDate: "",
      file: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser._node || !currentUser._key) {
      alert("कृपया लॉगिन करा (Please login)");
      return;
    }

    setLoading(true);
    try {
      if (!formData.lotteryNo) {
        alert("कृपया लॉटरी क्रमांक भरा (Please fill Lottery Number)");
        setLoading(false);
        return;
      }

      // If lottery number changed during edit, we might want to prevent it or handle it
      if (isEditing && editId !== formData.lotteryNo) {
        const confirmChange = window.confirm("लॉटरी क्रमांक बदलल्यास नवीन रेकॉर्ड तयार होईल. तुम्हाला खात्री आहे का?");
        if (!confirmChange) {
          setLoading(false);
          return;
        }
      }

      let fileUrl = formData.fileUrl || ""; // Keep old URL if no new file
      if (formData.file) {
        // Store in a folder named after the lottery number for better organization
        const storageRef = sRef(storage, `work_completions/${currentUser._key}/${formData.lotteryNo}/${formData.file.name}`);
        const uploadResult = await uploadBytes(storageRef, formData.file);
        fileUrl = await getDownloadURL(uploadResult.ref);
      } else if (isEditing) {
        // Find existing record to preserve fileUrl
        const existingRecord = tableData.find(item => item.lotteryNo === editId);
        if (existingRecord) fileUrl = existingRecord.fileUrl;
      }

      const workCompleteRef = ref(db, `${currentUser._node}/${currentUser._key}/work complete/${formData.lotteryNo}`);

      const payload = {
        workName: formData.workName,
        lotteryNo: formData.lotteryNo,
        price: formData.price,
        department: formData.department,
        duration: formData.duration,
        completionDate: formData.completionDate,
        fileUrl: fileUrl,
        status: isEditing ? (tableData.find(i => i.lotteryNo === editId)?.status || "पिंडिंग") : "पिंडिंग",
        submittedAt: isEditing ? (tableData.find(i => i.lotteryNo === editId)?.submittedAt || new Date().toISOString()) : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await set(workCompleteRef, payload);

      // If lottery number changed, delete the old node
      if (isEditing && editId !== formData.lotteryNo) {
        const oldRef = ref(db, `${currentUser._node}/${currentUser._key}/work complete/${editId}`);
        await set(oldRef, null);
      }

      alert(isEditing ? "तपशील यशस्वीरित्या अपडेट झाले!" : "तपशील यशस्वीरित्या जतन झाले!");
      setIsEditing(false);
      setEditId(null);
      setFormData({
        workName: "",
        lotteryNo: "",
        price: "",
        department: "",
        duration: "",
        completionDate: "",
        file: null,
      });
    } catch (error) {
      console.error("Error saving work details:", error);
      alert("तपशील जतन करताना त्रुटी आली: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-8">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">पासबुक अपडेट</h1>
          <p className="text-sm text-slate-500 mt-1">तुमच्या लॉटरी कामांचे तपशील येथे अपडेट करा</p>
        </div>
        <nav className="text-sm text-slate-400 bg-white px-4 py-2 rounded-full shadow-sm border">
          सांगली जिल्हा परिषद / <span className="text-slate-700 font-medium">पासबुक अपडेट</span>
        </nav>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden mb-8">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">

              {/* Work Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  लॉटरी पद्धतीने मिळालेल्या कामाचे नाव <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="workName"
                  placeholder="लॉटरी पद्धतीने मिळालेल्या कामाचे नाव"
                  value={formData.workName}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  required
                />
              </div>

              {/* Lottery No */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  लॉटरी क्रमांक
                </label>
                <input
                  type="text"
                  name="lotteryNo"
                  placeholder="लॉटरी क्रमांक"
                  value={formData.lotteryNo}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  कामाची किंमत <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="price"
                  placeholder="कामाची किंमत"
                  value={formData.price}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  required
                />
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  काम देणारा मंडळ / विभागाचे नाव <span className="text-red-500">*</span>
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  required
                >
                  <option value="">विभाग निवडा</option>
                  <option>आरोग्य विभाग</option>
                  <option>शिक्षण विभाग</option>
                  <option>लघु पाट बंधारे विभाग</option>
                  <option>ग्रामीण पाणी पुरवठा</option>
                  <option>बांधकाम विभाग</option>
                </select>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  काम पूर्ण करण्याची मुदत <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="duration"
                  placeholder="काम पूर्ण करण्याची मुदत (eg.1 Year.)"
                  value={formData.duration}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  required
                />
              </div>

              {/* Completion Date */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  काम पूर्ण केल्याचा दिनांक <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="completionDate"
                    value={formData.completionDate}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  वर्क कंप्लिशन <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  name="file"
                  onChange={handleChange}
                  className="w-full px-4 py-1.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all bg-slate-50 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  required
                />
              </div>

            </div>

            <div className="pt-4 flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className={`bg-blue-600 text-white px-10 py-3 rounded-lg font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {isEditing ? "Updating..." : "Submitting..."}
                  </>
                ) : (isEditing ? "Update Passbook" : "Submit")}
              </button>

              {isEditing && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="bg-slate-200 text-slate-700 px-8 py-3 rounded-lg font-bold hover:bg-slate-300 transition-all"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Action Table Section */}
        <div className="border-t border-slate-200 bg-slate-50/50 p-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4 mb-6">
            <div className="flex bg-white border border-slate-300 rounded-lg overflow-hidden shadow-sm w-full lg:w-auto">
              <button className="flex-1 lg:flex-none px-6 py-2.5 text-slate-600 hover:bg-slate-50 border-r border-slate-300 transition-colors flex items-center justify-center gap-2 font-medium bg-slate-700/5">
                <Copy size={16} /> Copy
              </button>
              <button className="flex-1 lg:flex-none px-6 py-2.5 text-slate-600 hover:bg-slate-50 border-r border-slate-300 transition-colors flex items-center justify-center gap-2 font-medium bg-slate-700/5">
                <FileSpreadsheet size={16} /> Excel
              </button>
              <button className="flex-1 lg:flex-none px-6 py-2.5 text-slate-600 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 font-medium bg-slate-700/5">
                <Printer size={16} /> Print
              </button>
            </div>

            <div className="relative w-full lg:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm">Sr.No. <Clock size={12} className="inline ml-1 opacity-40" /></th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm min-w-[200px]">कामाचे नाव</th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm">लॉटरी क्रमांक</th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm">कामाची किंमत</th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm">विभागाचे नाव</th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm">काम पूर्ण करण्याची मुदत</th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm">काम पूर्ण दिनांक</th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm text-center">स्थिती</th>
                  <th className="px-4 py-4 text-left border-r border-slate-200 font-bold text-slate-700 text-sm text-center">शेरा</th>
                  <th className="px-4 py-4 text-center font-bold text-slate-700 text-sm">एडिट पासबुक</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-10 text-center text-slate-400 font-medium">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  tableData.map((item, index) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-4 border-r border-slate-100 text-center font-medium text-slate-600">{index + 1}</td>
                      <td className="px-4 py-4 border-r border-slate-100 font-semibold text-slate-700">{item.workName}</td>
                      <td className="px-4 py-4 border-r border-slate-100 text-slate-600">{item.lotteryNo || "-"}</td>
                      <td className="px-4 py-4 border-r border-slate-100 text-slate-600 font-medium">{item.price}</td>
                      <td className="px-4 py-4 border-r border-slate-100 text-slate-600">{item.department}</td>
                      <td className="px-4 py-4 border-r border-slate-100 text-slate-600">{item.duration}</td>
                      <td className="px-4 py-4 border-r border-slate-100 text-slate-600">{item.completionDate}</td>
                      <td className="px-4 py-4 border-r border-slate-100 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ring-1 ${item.status === "Approved" ? "bg-green-100 text-green-700 ring-green-200" : "bg-yellow-100 text-yellow-700 ring-yellow-200"}`}>
                          {item.status || "पिंडिंग"}
                        </span>
                      </td>
                      <td className="px-4 py-4 border-r border-slate-100 text-center text-slate-400 italic text-xs">-</td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {item.fileUrl && (
                            <a
                              href={item.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-emerald-100 shadow-sm"
                              title="View Document"
                            >
                              <Eye size={16} />
                            </a>
                          )}
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-100 shadow-sm"
                            title="Edit Record"
                          >
                            <Edit2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer / Pagination */}
          <div className="mt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500 font-medium">
              Showing {tableData.length > 0 ? 1 : 0} to {tableData.length} of {tableData.length} entries
            </p>
            <div className="flex rounded-lg border border-slate-300 overflow-hidden shadow-sm bg-white">
              <button className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 border-r border-slate-300">Previous</button>
              <button className="px-4 py-2 text-sm font-bold bg-blue-600 text-white border-r border-blue-600">1</button>
              <button className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}